import React, { useState } from 'react';
import axios from 'axios';
import { Globe, Shield, Server, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DnsMonitor() {
  const [domain, setDomain] = useState('anlumax.co.za');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const checkDns = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/dns/check`, { params: { domain }, withCredentials: true });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check DNS');
    } finally {
      setLoading(false);
    }
  };

  const records = result?.records || {};
  const ssl = result?.ssl || {};

  return (
    <div className="p-8" data-testid="dns-monitor">
      <h1 className="text-2xl font-bold text-white mb-8" data-testid="dns-title">DNS & Domain Health</h1>

      {/* Domain Input */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="Enter domain (e.g., anlumax.co.za)"
              className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="dns-domain-input"
            />
          </div>
          <button
            onClick={checkDns}
            disabled={loading || !domain}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            data-testid="dns-check-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Checking...' : 'Check DNS'}
          </button>
        </div>
        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
      </div>

      {result && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* SSL Certificate */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="ssl-status">
            <div className="flex items-center gap-3 mb-5">
              <Shield size={20} className={ssl.valid ? 'text-emerald-400' : 'text-red-400'} />
              <h2 className="text-lg font-semibold text-white">SSL Certificate</h2>
            </div>

            {ssl.valid ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Valid & Active</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400 mb-1">Issuer</p>
                    <p className="text-white">{ssl.issuer?.organizationName || ssl.issuer?.commonName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">Expires</p>
                    <p className="text-white">{ssl.expires || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-400 mb-1">Days Remaining</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-bold ${ssl.days_remaining > 30 ? 'text-emerald-400' : ssl.days_remaining > 7 ? 'text-amber-400' : 'text-red-400'}`}>
                        {ssl.days_remaining}
                      </span>
                      {ssl.days_remaining <= 30 && <AlertTriangle size={16} className="text-amber-400" />}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle size={16} className="text-red-400" />
                <span className="text-red-400">{ssl.error || 'No valid SSL certificate found'}</span>
              </div>
            )}
          </div>

          {/* DNS Records */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="dns-records">
            <div className="flex items-center gap-3 mb-5">
              <Server size={20} className="text-blue-400" />
              <h2 className="text-lg font-semibold text-white">DNS Records</h2>
            </div>

            <div className="space-y-4">
              {['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS'].map(type => {
                const recs = records[type] || [];
                return (
                  <div key={type} className="border-b border-slate-700/50 pb-3 last:border-0" data-testid={`dns-record-${type}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-mono font-medium text-blue-400">{type}</span>
                      <span className="text-xs text-slate-500">{recs.length} record{recs.length !== 1 ? 's' : ''}</span>
                    </div>
                    {recs.length > 0 ? (
                      <div className="space-y-1">
                        {recs.map((r, i) => (
                          <p key={i} className="text-sm text-slate-300 font-mono bg-slate-700/30 px-2 py-1 rounded truncate">{r}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">No records found</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!result && !loading && (
        <div className="text-center py-16 text-slate-500">
          <Globe size={48} className="mx-auto mb-4 opacity-30" />
          <p>Enter a domain and click "Check DNS" to view records and SSL status</p>
        </div>
      )}
    </div>
  );
}
